import {
    doc,
    serverTimestamp,
    writeBatch,
    increment,
    collection,
    query,
    where,
    getDocs,
    updateDoc,
    deleteDoc,
    collectionGroup,
    limit,
    orderBy,
    startAt,
    endAt,
    DocumentData,
    FieldValue
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { EnrollmentError, ValidationError } from '@/shared/api/errors/ApiError';
import ServiceBase from '@/shared/api/base/ServiceBase';
import { getFirestoreTimestamps } from '@/shared/utils/api/timestampHelper';
import {
    COURSE_IDS,
    COURSE_PRICING,
    ENROLLMENT_STATUS,
    PAYMENT_STATUS,
    ACCESS_STATUS,
    ADMIN_CONFIG
} from '@/shared/constants';

export interface Enrollment {
    id: string;
    userId: string;
    courseId: string;
    enrollmentDate: any;
    status: string;
    paymentStatus: string;
    accessStatus: string;
    totalAmount: number;
    amountPaid: number;
    amountDue: number;
    upfrontAmount?: number;
    remainingAmount?: number;
    totalMinutesOverall?: number;
    totalMinutesDaily?: number;
    examStatus?: string;
    examAttempts?: any[];
    certificateGenerated?: boolean;
    progress?: number;
    lastAccessedAt?: any;
    completedAt?: any;
    isAdminEnrollment?: boolean;
    parentEnrollmentId?: string;
    isComponentOfBundle?: boolean;
    paymentPlan?: string;
    splitPaymentStatus?: string;
    createdAt?: any;
    updatedAt?: any;
}

export interface UserWithEnrollments {
    userId: string;
    displayName: string;
    email: string;
    enrollments: Enrollment[];
}

class EnrollmentService extends ServiceBase {
    constructor() {
        super('EnrollmentService');
    }

    async createEnrollment(userId: string, courseId: string, userEmail = ''): Promise<Enrollment> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            if (userEmail && typeof userEmail !== 'string') {
                throw new ValidationError('userEmail must be a string');
            }

            let existingEnrollment: Enrollment | null = null;
            try {
                existingEnrollment = await this.getDoc(`users/${userId}/enrollments`, courseId) as Enrollment;
            } catch (error: any) {
                if (error.code !== 'NOT_FOUND') {
                    throw error;
                }
            }

            if (existingEnrollment) {
                return existingEnrollment;
            }

            let pricing = (COURSE_PRICING as any)[courseId];

            // E2E Test Mock Injection - Handle missing pricing for mock courses
            if (!pricing && typeof window !== 'undefined' && (window as any).MOCK_COURSES) {
                const mockCourse = ((window as any).MOCK_COURSES as any[]).find(c => c.id === courseId);
                if (mockCourse) {
                    pricing = {
                        total: mockCourse.price || 0,
                        upfront: mockCourse.price || 0,
                        remaining: 0,
                        currency: 'usd',
                        description: mockCourse.description
                    };
                }
            }

            if (!pricing) {
                throw new EnrollmentError('Invalid course ID', courseId);
            }

            const isAdmin = userEmail ? ADMIN_CONFIG.AUTO_ENROLL_EMAILS.includes(userEmail.toLowerCase()) : false;
            const isFree = pricing.total === 0;

            const enrollmentData: Partial<Enrollment> = {
                userId,
                courseId,
                enrollmentDate: serverTimestamp(),
                status: (isAdmin || isFree) ? ENROLLMENT_STATUS.ACTIVE : ENROLLMENT_STATUS.PENDING_PAYMENT,
                paymentStatus: (isAdmin || isFree) ? PAYMENT_STATUS.COMPLETED : PAYMENT_STATUS.PENDING,
                accessStatus: (isAdmin || isFree) ? ACCESS_STATUS.UNLOCKED : ACCESS_STATUS.LOCKED,
                totalAmount: pricing.total,
                amountPaid: (isAdmin || isFree) ? pricing.total : 0,
                amountDue: (isAdmin || isFree) ? 0 : pricing.upfront || pricing.total,
                upfrontAmount: pricing.upfront,
                remainingAmount: (isAdmin || isFree) ? 0 : pricing.remaining,
                totalMinutesOverall: 0,
                totalMinutesDaily: 0,
                examStatus: 'NOT_STARTED',
                examAttempts: [],
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                isAdminEnrollment: isAdmin,
                ...getFirestoreTimestamps()
            };

            await this.setDoc(`users/${userId}/enrollments`, courseId, enrollmentData);
            this.log(`Created enrollment for user ${userId} in course ${courseId}`);

            this.invalidateEnrollmentCache(userId, courseId);

            return {
                id: courseId,
                ...enrollmentData
            } as Enrollment;
        } catch (error) {
            this.logError(error, { method: 'createEnrollment', userId, courseId });
            throw error;
        }
    }

    async createCompletePackageEnrollment(userId: string, userEmail = ''): Promise<{ completePackage: Enrollment; online: Enrollment; behindWheel: Enrollment }> {
        try {
            this.validate.validateUserId(userId);
            if (typeof userEmail !== 'string') {
                throw new ValidationError('userEmail must be a string');
            }

            const batch = writeBatch(db);
            const isAdmin = ADMIN_CONFIG.AUTO_ENROLL_EMAILS.includes(userEmail.toLowerCase());

            const completeEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.COMPLETE);
            const completePricing = (COURSE_PRICING as any)[COURSE_IDS.COMPLETE];

            const completeEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.COMPLETE,
                enrollmentDate: serverTimestamp(),
                status: isAdmin ? ENROLLMENT_STATUS.ACTIVE : ENROLLMENT_STATUS.PENDING_PAYMENT,
                paymentStatus: isAdmin ? PAYMENT_STATUS.COMPLETED : PAYMENT_STATUS.PENDING,
                accessStatus: isAdmin ? ACCESS_STATUS.UNLOCKED : ACCESS_STATUS.LOCKED,
                totalAmount: completePricing.total,
                amountPaid: isAdmin ? completePricing.total : 0,
                amountDue: isAdmin ? 0 : completePricing.upfront,
                upfrontAmount: completePricing.upfront,
                remainingAmount: isAdmin ? 0 : completePricing.remaining,
                totalMinutesOverall: 0,
                totalMinutesDaily: 0,
                examStatus: 'NOT_STARTED',
                examAttempts: [],
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                isAdminEnrollment: isAdmin,
                ...getFirestoreTimestamps()
            };

            batch.set(completeEnrollmentRef, completeEnrollmentData);

            const onlineEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.ONLINE);
            const onlineEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.ONLINE,
                enrollmentDate: serverTimestamp(),
                status: isAdmin ? ENROLLMENT_STATUS.ACTIVE : ENROLLMENT_STATUS.PENDING_PAYMENT,
                paymentStatus: isAdmin ? PAYMENT_STATUS.COMPLETED : PAYMENT_STATUS.PENDING,
                accessStatus: isAdmin ? ACCESS_STATUS.UNLOCKED : ACCESS_STATUS.LOCKED,
                totalAmount: 0,
                amountPaid: 0,
                amountDue: 0,
                parentEnrollmentId: COURSE_IDS.COMPLETE,
                isComponentOfBundle: true,
                totalMinutesOverall: 0,
                totalMinutesDaily: 0,
                examStatus: 'NOT_STARTED',
                examAttempts: [],
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                isAdminEnrollment: isAdmin,
                ...getFirestoreTimestamps()
            };

            batch.set(onlineEnrollmentRef, onlineEnrollmentData);

            const btwEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.BEHIND_WHEEL);
            const btwEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.BEHIND_WHEEL,
                enrollmentDate: serverTimestamp(),
                status: isAdmin ? ENROLLMENT_STATUS.ACTIVE : ENROLLMENT_STATUS.PENDING_PAYMENT,
                paymentStatus: isAdmin ? PAYMENT_STATUS.COMPLETED : PAYMENT_STATUS.PENDING,
                accessStatus: isAdmin ? ACCESS_STATUS.UNLOCKED : ACCESS_STATUS.LOCKED,
                totalAmount: 0,
                amountPaid: 0,
                amountDue: 0,
                parentEnrollmentId: COURSE_IDS.COMPLETE,
                isComponentOfBundle: true,
                totalMinutesOverall: 0,
                totalMinutesDaily: 0,
                examStatus: 'NOT_STARTED',
                examAttempts: [],
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                isAdminEnrollment: isAdmin,
                ...getFirestoreTimestamps()
            };

            batch.set(btwEnrollmentRef, btwEnrollmentData);
            await batch.commit();
            this.log(`Created complete package enrollment for user ${userId}`);

            this.invalidateEnrollmentCache(userId, COURSE_IDS.COMPLETE);
            this.invalidateEnrollmentCache(userId, COURSE_IDS.ONLINE);
            this.invalidateEnrollmentCache(userId, COURSE_IDS.BEHIND_WHEEL);

            return {
                completePackage: { id: COURSE_IDS.COMPLETE, ...completeEnrollmentData } as Enrollment,
                online: { id: COURSE_IDS.ONLINE, ...onlineEnrollmentData } as Enrollment,
                behindWheel: { id: COURSE_IDS.BEHIND_WHEEL, ...btwEnrollmentData } as Enrollment
            };
        } catch (error) {
            this.logError(error, { method: 'createCompletePackageEnrollment', userId });
            throw error;
        }
    }

    async getEnrollment(userId: string, courseId: string): Promise<Enrollment | null> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const cacheKey = `enrollment_${userId}_${courseId}`;
            const cached = this.cache.get<Enrollment>(cacheKey);
            if (cached) return cached;

            const enrollment = await this.getDoc(`users/${userId}/enrollments`, courseId) as Enrollment;

            if (!enrollment) {
                return null;
            }

            const enrollmentData: Enrollment = {
                ...enrollment,
                id: courseId,
                totalAmount: Number(enrollment.totalAmount ?? 0),
                amountPaid: Number(enrollment.amountPaid ?? 0),
                amountDue: Number(enrollment.amountDue ?? 0)
            };

            this.cache.set(cacheKey, enrollmentData, 5 * 60 * 1000); // 5 min cache
            return enrollmentData;
        } catch (error: any) {
            if (error.code === 'NOT_FOUND') {
                return null;
            }
            this.logError(error, { method: 'getEnrollment', userId, courseId });
            throw error;
        }
    }

    async getUserEnrollments(userId: string): Promise<Enrollment[]> {
        try {
            this.validate.validateUserId(userId);

            const cacheKey = `user_enrollments_${userId}`;
            const cached = this.cache.get<Enrollment[]>(cacheKey);
            if (cached) return cached;

            const enrollments = await this.getCollection(`users/${userId}/enrollments`) as Enrollment[];

            const enrollmentList = enrollments.map(enrollment => ({
                ...enrollment,
                id: enrollment.id,
                totalAmount: Number(enrollment.totalAmount ?? 0),
                amountPaid: Number(enrollment.amountPaid ?? 0),
                amountDue: Number(enrollment.amountDue ?? 0)
            }));

            this.cache.set(cacheKey, enrollmentList, 5 * 60 * 1000); // 5 min cache
            return enrollmentList;
        } catch (error) {
            this.logError(error, { method: 'getUserEnrollments', userId });
            throw error;
        }
    }

    invalidateEnrollmentCache(userId: string, courseId: string): void {
        this.cache.invalidate(`enrollment_${userId}_${courseId}`);
        this.cache.invalidate(`user_enrollments_${userId}`);
    }

    async updateEnrollmentAfterPayment(userId: string, courseId: string, paymentAmount: number | string, _paymentType = 'upfront'): Promise<any> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const enrollment = await this.getEnrollment(userId, courseId);
            if (!enrollment) {
                throw new EnrollmentError('Enrollment not found', courseId);
            }

            const amountPaidNum = Number(paymentAmount);
            const amountDue = Math.max(0, Number(enrollment.amountDue || 0) - amountPaidNum);

            const batch = writeBatch(db);
            const enrollmentRef = doc(db, `users/${userId}/enrollments`, courseId);

            const updates: any = {
                amountPaid: increment(amountPaidNum),
                amountDue: increment(-amountPaidNum),
                paymentStatus: amountDue === 0 ? PAYMENT_STATUS.COMPLETED : PAYMENT_STATUS.PARTIAL,
                accessStatus: amountDue === 0 ? ACCESS_STATUS.UNLOCKED : ACCESS_STATUS.LOCKED,
                updatedAt: serverTimestamp()
            };

            if (amountDue === 0) {
                updates.status = ENROLLMENT_STATUS.ACTIVE;
            }

            batch.update(enrollmentRef, updates);
            await batch.commit();

            this.log(`Updated enrollment after payment: user ${userId}, course ${courseId}, amount ${paymentAmount}`);
            this.invalidateEnrollmentCache(userId, courseId);

            return {
                amountPaid: amountPaidNum,
                amountDue,
                paymentStatus: updates.paymentStatus,
                accessStatus: updates.accessStatus,
                status: updates.status || enrollment.status
            };
        } catch (error) {
            this.logError(error, { method: 'updateEnrollmentAfterPayment', userId, courseId });
            throw error;
        }
    }

    async updateCertificateStatus(userId: string, courseId: string, certificateGenerated = true): Promise<any> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const updates = {
                certificateGenerated,
                updatedAt: serverTimestamp()
            };

            await this.updateDoc(`users/${userId}/enrollments`, courseId, updates);
            this.log(`Updated certificate status for user ${userId} in course ${courseId}`);
            this.invalidateEnrollmentCache(userId, courseId);

            return updates;
        } catch (error) {
            this.logError(error, { method: 'updateCertificateStatus', userId, courseId });
            throw error;
        }
    }

    async checkCourseAccess(userId: string, courseId: string): Promise<any> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const enrollment = await this.getEnrollment(userId, courseId);
            if (!enrollment) {
                return { hasAccess: false, reason: 'No enrollment found' };
            }

            let hasAccess = enrollment.accessStatus === ACCESS_STATUS.UNLOCKED;
            const isActive = enrollment.status === ENROLLMENT_STATUS.ACTIVE;

            // Check enrollment expiration (6 months / 180 days limit)
            let isExpired = false;
            if (enrollment.enrollmentDate) {
                const enrollmentTime = (enrollment.enrollmentDate as any).toDate
                    ? (enrollment.enrollmentDate as any).toDate().getTime()
                    : new Date(enrollment.enrollmentDate).getTime();

                const sixMonthsMs = 180 * 24 * 60 * 60 * 1000;
                if (Date.now() - enrollmentTime > sixMonthsMs) {
                    isExpired = true;
                }
            }

            if (hasAccess && courseId === COURSE_IDS.BEHIND_WHEEL) {
                const onlineEnrollment = await this.getEnrollment(userId, COURSE_IDS.ONLINE);
                if (onlineEnrollment && !onlineEnrollment.certificateGenerated) {
                    hasAccess = false;
                }
            }

            return {
                hasAccess: hasAccess && isActive && !isExpired,
                accessStatus: enrollment.accessStatus,
                enrollmentStatus: enrollment.status,
                paymentStatus: enrollment.paymentStatus,
                requiresOnlineCertificate: courseId === COURSE_IDS.BEHIND_WHEEL && !enrollment.parentEnrollmentId && (enrollment.accessStatus === ACCESS_STATUS.UNLOCKED),
                isExpired
            };
        } catch (error) {
            this.logError(error, { method: 'checkCourseAccess', userId, courseId });
            throw error;
        }
    }

    async autoEnrollAdmin(userId: string, userEmail: string): Promise<{ enrolled: boolean }> {
        try {
            this.validate.validateUserId(userId);
            if (typeof userEmail !== 'string') {
                throw new ValidationError('userEmail must be a string');
            }

            const isAdmin = ADMIN_CONFIG.AUTO_ENROLL_EMAILS.includes(userEmail.toLowerCase());
            if (!isAdmin) {
                this.log(`User ${userId} email not in admin list, skipping auto-enrollment`);
                return { enrolled: false };
            }

            await this.createCompletePackageEnrollment(userId, userEmail);
            this.log(`Auto-enrolled admin user ${userId}`);

            return { enrolled: true };
        } catch (error) {
            this.logError(error, { method: 'autoEnrollAdmin', userId });
            throw error;
        }
    }

    async resetEnrollmentToPending(userId: string, courseId: string): Promise<any> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const updates = {
                status: ENROLLMENT_STATUS.PENDING_PAYMENT,
                accessStatus: ACCESS_STATUS.LOCKED,
                paymentStatus: PAYMENT_STATUS.PENDING,
                amountPaid: 0,
                updatedAt: serverTimestamp()
            };

            await this.updateDoc(`users/${userId}/enrollments`, courseId, updates);
            this.log(`Reset enrollment to pending for user ${userId}, course ${courseId}`);

            return updates;
        } catch (error) {
            this.logError(error, { method: 'resetEnrollmentToPending', userId, courseId });
            throw error;
        }
    }

    async resetUserEnrollmentsToPending(userId: string): Promise<{ updated: number }> {
        try {
            this.validate.validateUserId(userId);

            const userEnrollments = await this.getUserEnrollments(userId);
            const batch = writeBatch(db);

            const updates = {
                status: ENROLLMENT_STATUS.PENDING_PAYMENT,
                accessStatus: ACCESS_STATUS.LOCKED,
                paymentStatus: PAYMENT_STATUS.PENDING,
                amountPaid: 0,
                updatedAt: serverTimestamp()
            };

            userEnrollments.forEach(enrollment => {
                const ref = doc(db, 'users', userId, 'enrollments', enrollment.id);
                batch.update(ref, updates);
            });

            await batch.commit();
            this.log(`Reset all enrollments to pending for user ${userId}`);

            return { updated: userEnrollments.length };
        } catch (error) {
            this.logError(error, { method: 'resetUserEnrollmentsToPending', userId });
            throw error;
        }
    }

    async getAllUsersWithEnrollments(limitCount = 100): Promise<UserWithEnrollments[]> {
        try {
            const enrollmentsQuery = query(
                collectionGroup(db, 'enrollments'),
                orderBy('enrollmentDate', 'desc'),
                limit(limitCount)
            );
            const enrollmentsSnapshot = await getDocs(enrollmentsQuery);

            const userIds = [...new Set(enrollmentsSnapshot.docs.map(doc => doc.data().userId))].filter(Boolean) as string[];

            const usersById: any = {};
            for (let i = 0; i < userIds.length; i += 10) {
                const chunk = userIds.slice(i, i + 10);
                const usersQuery = query(collection(db, 'users'), where('__name__', 'in', chunk));
                const usersSnapshot = await getDocs(usersQuery);
                usersSnapshot.docs.forEach(doc => {
                    usersById[doc.id] = doc.data();
                });
            }

            const enrollmentsByUser: any = {};
            enrollmentsSnapshot.docs.forEach(doc => {
                const data = doc.data();
                const userId = data.userId;
                if (!userId) return;

                if (!enrollmentsByUser[userId]) {
                    enrollmentsByUser[userId] = [];
                }
                enrollmentsByUser[userId].push({
                    id: doc.id,
                    ...data,
                    totalAmount: Number(data.totalAmount ?? 0),
                    amountPaid: Number(data.amountPaid ?? 0),
                    amountDue: Number(data.amountDue ?? 0)
                });
            });

            const usersWithEnrollments = Object.keys(enrollmentsByUser).map(userId => {
                const user = usersById[userId] || {};
                return {
                    userId,
                    displayName: user.displayName || user.name || 'Unknown User',
                    email: user.email || 'N/A',
                    enrollments: enrollmentsByUser[userId]
                };
            });

            this.log(`Retrieved optimized enrollment data for ${usersWithEnrollments.length} active users`);
            return usersWithEnrollments;
        } catch (error) {
            this.logError(error, { method: 'getAllUsersWithEnrollments' });
            throw error;
        }
    }

    async searchUsersWithEnrollments(searchTerm = '', limitCount = 50): Promise<UserWithEnrollments[]> {
        try {
            const usersRef = collection(db, 'users');
            let usersQuery;

            if (searchTerm) {
                usersQuery = query(
                    usersRef,
                    orderBy('displayName'),
                    startAt(searchTerm),
                    endAt(searchTerm + '\uf8ff'),
                    limit(limitCount)
                );
            } else {
                usersQuery = query(usersRef, orderBy('displayName'), limit(limitCount));
            }

            const usersSnapshot = await getDocs(usersQuery);
            const userIds = usersSnapshot.docs.map(doc => doc.id);
            const usersById: any = {};
            usersSnapshot.docs.forEach(doc => {
                usersById[doc.id] = doc.data();
            });

            if (userIds.length === 0) return [];

            const enrollmentsByUser: any = {};

            for (let i = 0; i < userIds.length; i += 10) {
                const chunk = userIds.slice(i, i + 10);
                const enrollmentsQuery = query(
                    collectionGroup(db, 'enrollments'),
                    where('userId', 'in', chunk)
                );
                const enrollmentsSnapshot = await getDocs(enrollmentsQuery);

                enrollmentsSnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    const userId = data.userId;
                    if (!enrollmentsByUser[userId]) {
                        enrollmentsByUser[userId] = [];
                    }
                    enrollmentsByUser[userId].push({
                        id: doc.id,
                        ...data,
                        totalAmount: Number(data.totalAmount ?? 0),
                        amountPaid: Number(data.amountPaid ?? 0),
                        amountDue: Number(data.amountDue ?? 0)
                    });
                });
            }

            const usersWithEnrollments = userIds.map(userId => {
                const user = usersById[userId];
                return {
                    userId,
                    displayName: user.displayName || user.name || 'Unknown User',
                    email: user.email || 'N/A',
                    enrollments: enrollmentsByUser[userId] || []
                };
            }).filter(u => u.enrollments.length > 0);

            this.log(`Searched enrollment data for ${usersWithEnrollments.length} users matching "${searchTerm}"`);
            return usersWithEnrollments;
        } catch (error) {
            this.logError(error, { method: 'searchUsersWithEnrollments', searchTerm });
            throw error;
        }
    }

    async createPaidEnrollment(userId: string, courseId: string, paidAmount: number, _userEmail = ''): Promise<Enrollment> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);
            if (typeof paidAmount !== 'number' || paidAmount < 0) {
                throw new ValidationError('paidAmount must be a non-negative number');
            }

            const pricing = (COURSE_PRICING as any)[courseId];

            const isMockCourse = typeof window !== 'undefined' && (window as any).MOCK_COURSES &&
                ((window as any).MOCK_COURSES as any[]).some(c => c.id === courseId);

            if (!pricing && !isMockCourse) {
                throw new EnrollmentError('Invalid course ID', courseId);
            }

            const coursePricing = pricing || (isMockCourse ? { total: paidAmount, upfront: paidAmount, remaining: 0 } : null);

            const enrollmentData: Partial<Enrollment> = {
                userId,
                courseId,
                enrollmentDate: serverTimestamp(),
                status: ENROLLMENT_STATUS.ACTIVE,
                paymentStatus: PAYMENT_STATUS.COMPLETED,
                accessStatus: ACCESS_STATUS.UNLOCKED,
                totalAmount: coursePricing.total,
                amountPaid: Number(paidAmount),
                amountDue: Math.max(0, coursePricing.total - Number(paidAmount)),
                upfrontAmount: coursePricing.upfront,
                remainingAmount: Math.max(0, coursePricing.remaining - Number(paidAmount)),
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                ...getFirestoreTimestamps()
            };

            await this.setDoc(`users/${userId}/enrollments`, courseId, enrollmentData);
            this.log(`Created paid enrollment for user ${userId} in course ${courseId}`);

            return {
                id: courseId,
                ...enrollmentData
            } as Enrollment;
        } catch (error) {
            this.logError(error, { method: 'createPaidEnrollment', userId, courseId });
            throw error;
        }
    }

    async createPaidCompletePackageEnrollment(userId: string, paidAmount: number, _userEmail = ''): Promise<{ completePackage: Enrollment; online: Enrollment; behindWheel: Enrollment }> {
        try {
            this.validate.validateUserId(userId);
            if (typeof paidAmount !== 'number' || paidAmount < 0) {
                throw new ValidationError('paidAmount must be a non-negative number');
            }

            const batch = writeBatch(db);
            const completePricing = (COURSE_PRICING as any)[COURSE_IDS.COMPLETE];

            const completeEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.COMPLETE);
            const completeEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.COMPLETE,
                enrollmentDate: serverTimestamp(),
                status: ENROLLMENT_STATUS.ACTIVE,
                paymentStatus: PAYMENT_STATUS.COMPLETED,
                accessStatus: ACCESS_STATUS.UNLOCKED,
                totalAmount: completePricing.total,
                amountPaid: Number(paidAmount),
                amountDue: Math.max(0, completePricing.total - Number(paidAmount)),
                upfrontAmount: completePricing.upfront,
                remainingAmount: Math.max(0, completePricing.remaining - Number(paidAmount)),
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                ...getFirestoreTimestamps()
            };

            batch.set(completeEnrollmentRef, completeEnrollmentData);

            const onlineEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.ONLINE);
            const onlineEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.ONLINE,
                enrollmentDate: serverTimestamp(),
                status: ENROLLMENT_STATUS.ACTIVE,
                paymentStatus: PAYMENT_STATUS.COMPLETED,
                accessStatus: ACCESS_STATUS.UNLOCKED,
                totalAmount: 0,
                amountPaid: 0,
                amountDue: 0,
                parentEnrollmentId: COURSE_IDS.COMPLETE,
                isComponentOfBundle: true,
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                ...getFirestoreTimestamps()
            };

            batch.set(onlineEnrollmentRef, onlineEnrollmentData);

            const btwEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.BEHIND_WHEEL);
            const btwEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.BEHIND_WHEEL,
                enrollmentDate: serverTimestamp(),
                status: ENROLLMENT_STATUS.ACTIVE,
                paymentStatus: PAYMENT_STATUS.COMPLETED,
                accessStatus: ACCESS_STATUS.UNLOCKED,
                totalAmount: 0,
                amountPaid: 0,
                amountDue: 0,
                parentEnrollmentId: COURSE_IDS.COMPLETE,
                isComponentOfBundle: true,
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                ...getFirestoreTimestamps()
            };

            batch.set(btwEnrollmentRef, btwEnrollmentData);
            await batch.commit();
            this.log(`Created paid complete package enrollment for user ${userId}`);

            return {
                completePackage: { id: COURSE_IDS.COMPLETE, ...completeEnrollmentData } as Enrollment,
                online: { id: COURSE_IDS.ONLINE, ...onlineEnrollmentData } as Enrollment,
                behindWheel: { id: COURSE_IDS.BEHIND_WHEEL, ...btwEnrollmentData } as Enrollment
            };
        } catch (error) {
            this.logError(error, { method: 'createPaidCompletePackageEnrollment', userId });
            throw error;
        }
    }

    async createPaidCompletePackageSplit(userId: string, upfrontAmount: number, _userEmail = ''): Promise<{ completePackage: Enrollment; online: Enrollment; behindWheel: Enrollment }> {
        try {
            this.validate.validateUserId(userId);
            if (typeof upfrontAmount !== 'number' || upfrontAmount <= 0) {
                throw new ValidationError('upfrontAmount must be a positive number');
            }

            const batch = writeBatch(db);
            const completePricing = (COURSE_PRICING as any)[COURSE_IDS.COMPLETE];

            const completeEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.COMPLETE);
            const completeEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.COMPLETE,
                enrollmentDate: serverTimestamp(),
                status: ENROLLMENT_STATUS.ACTIVE,
                paymentStatus: PAYMENT_STATUS.PARTIAL,
                accessStatus: ACCESS_STATUS.UNLOCKED,
                totalAmount: completePricing.total,
                amountPaid: Number(upfrontAmount),
                amountDue: 450,
                upfrontAmount: completePricing.upfront,
                remainingAmount: 450,
                paymentPlan: 'split',
                splitPaymentStatus: 'awaiting_certificate',
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                ...getFirestoreTimestamps()
            };

            batch.set(completeEnrollmentRef, completeEnrollmentData);

            const onlineEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.ONLINE);
            const onlineEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.ONLINE,
                enrollmentDate: serverTimestamp(),
                status: ENROLLMENT_STATUS.ACTIVE,
                paymentStatus: PAYMENT_STATUS.COMPLETED,
                accessStatus: ACCESS_STATUS.UNLOCKED,
                totalAmount: 0,
                amountPaid: 0,
                amountDue: 0,
                parentEnrollmentId: COURSE_IDS.COMPLETE,
                isComponentOfBundle: true,
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                ...getFirestoreTimestamps()
            };

            batch.set(onlineEnrollmentRef, onlineEnrollmentData);

            const btwEnrollmentRef = doc(db, 'users', userId, 'enrollments', COURSE_IDS.BEHIND_WHEEL);
            const btwEnrollmentData: Partial<Enrollment> = {
                userId,
                courseId: COURSE_IDS.BEHIND_WHEEL,
                enrollmentDate: serverTimestamp(),
                status: ENROLLMENT_STATUS.PENDING_PAYMENT,
                paymentStatus: PAYMENT_STATUS.PENDING,
                accessStatus: ACCESS_STATUS.LOCKED,
                totalAmount: 450,
                amountPaid: 0,
                amountDue: 450,
                parentEnrollmentId: COURSE_IDS.COMPLETE,
                isComponentOfBundle: true,
                certificateGenerated: false,
                progress: 0,
                lastAccessedAt: null,
                completedAt: null,
                ...getFirestoreTimestamps()
            };

            batch.set(btwEnrollmentRef, btwEnrollmentData);
            await batch.commit();

            return {
                completePackage: { id: COURSE_IDS.COMPLETE, ...completeEnrollmentData } as Enrollment,
                online: { id: COURSE_IDS.ONLINE, ...onlineEnrollmentData } as Enrollment,
                behindWheel: { id: COURSE_IDS.BEHIND_WHEEL, ...btwEnrollmentData } as Enrollment
            };
        } catch (error) {
            this.logError(error, { method: 'createPaidCompletePackageSplit', userId });
            throw error;
        }
    }

    async leaveCourse(userId: string, courseId: string): Promise<{ success: boolean; message: string }> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            await this.deleteDoc(`users/${userId}/enrollments`, courseId);
            this.log(`Successfully removed enrollment for user ${userId} in course ${courseId}`);
            this.invalidateEnrollmentCache(userId, courseId);

            return {
                success: true,
                message: `Successfully removed enrollment from course ${courseId}`
            };
        } catch (error) {
            this.logError(error, { method: 'leaveCourse', userId, courseId });
            throw error;
        }
    }
}

const enrollmentServices = new EnrollmentService();

export const createEnrollment = (u: any, c: any, e: any) => enrollmentServices.createEnrollment(u, c, e);
export const createCompletePackageEnrollment = (u: any, e: any) => enrollmentServices.createCompletePackageEnrollment(u, e);
export const getEnrollment = (u: any, c: any) => enrollmentServices.getEnrollment(u, c);
export const getUserEnrollments = (u: any) => enrollmentServices.getUserEnrollments(u);
export const updateEnrollmentAfterPayment = (u: any, c: any, a: any) => enrollmentServices.updateEnrollmentAfterPayment(u, c, a);
export const updateCertificateStatus = (u: any, c: any, s: any) => enrollmentServices.updateCertificateStatus(u, c, s);
export const checkCourseAccess = (u: any, c: any) => enrollmentServices.checkCourseAccess(u, c);
export const autoEnrollAdmin = (u: any, e: any) => enrollmentServices.autoEnrollAdmin(u, e);
export const resetEnrollmentToPending = (u: any, c: any) => enrollmentServices.resetEnrollmentToPending(u, c);
export const resetUserEnrollmentsToPending = (u: any) => enrollmentServices.resetUserEnrollmentsToPending(u);
export const getAllUsersWithEnrollments = (l: any) => enrollmentServices.getAllUsersWithEnrollments(l);
export const searchUsersWithEnrollments = (t: any, l: any) => enrollmentServices.searchUsersWithEnrollments(t, l);
export const createPaidEnrollment = (u: any, c: any, a: any) => enrollmentServices.createPaidEnrollment(u, c, a);
export const createPaidCompletePackageEnrollment = (u: any, a: any) => enrollmentServices.createPaidCompletePackageEnrollment(u, a);
export const createPaidCompletePackageSplit = (u: any, a: any) => enrollmentServices.createPaidCompletePackageSplit(u, a);
export const leaveCourse = (u: any, c: any) => enrollmentServices.leaveCourse(u, c);

export default enrollmentServices;
